import { ChangeEvent, useEffect, useState } from "react";
import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  Typography,
  Chip,
  CardMedia,
  Input,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SwitchToggle from "../../components/SwitchToggle";
import CommentIcon from "@mui/icons-material/Comment";
import SelectUpload from "../../Modals/select_upload";
import {
  useAddProductMutation,
  useLazyGetAttributesQuery,
  useLazyGetProductByIdQuery,
  useUpdateProductMutation,
} from "../../service/product";
import { showError, showToast } from "../../constants/toasts";
import Loader from "../../constants/Loader";
import {
  useLazyGetCategoryQuery,
  useLazyGetSubCategoryQuery,
} from "../../service/main";
import { Attribute, Category, Product, VendorStore } from "../../types/General";
import { useFormik } from "formik";
import * as Yup from "yup";
import { UploadMedia } from "../../utils/mediaUpload";
import { getFromStorage, STORAGE_KEYS } from "../../helpers";
import { User } from "../../types/User";
import { useLazyGetProfileQuery } from "../../service/auth";
import { isNumber } from "../../utils/validations";

const AddProductEnhanced = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [getProductById] = useLazyGetProductByIdQuery();
  const [getAttribute] = useLazyGetAttributesQuery();
  const [getCategory] = useLazyGetCategoryQuery();
  const [getSubCategory] = useLazyGetSubCategoryQuery();
  const [addProduct] = useAddProductMutation();
  const [getProfile] = useLazyGetProfileQuery();
  const [updateProduct] = useUpdateProductMutation();

  const userData = getFromStorage(STORAGE_KEYS.userData);
  const parsedUserData: User | null = userData
    ? (JSON.parse(userData) as User)
    : null;

  const [loading, setLoading] = useState(false);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subCategories, setSubCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>("");
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [vendorData, setVendorData] = useState<VendorStore | null>(null);
  const [productData, setProductData] = useState<Product | null>(null);

  const [variants, setVariants] = useState<
    {
      attributes: { attributeId: string; value: string }[];
      variantName: string;
      price: string;
      discountedPrice: string;
      quantity: number;
      maxPurchaseLimit: number;
      thresholdLimit: number;
      media: { url: string; type: "image" | "video" }[];
    }[]
  >([]);

  const [selectedAttributes, setSelectedAttributes] = useState<{
    [attributeId: string]: string[];
  }>({});

  console.log(selectedAttributes, "selectedAttributes");

  const [openModal, setOpenModal] = useState(false);
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const [image, setImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [isFeatured, setIsFeatured] = useState(false);
  const [error, setError] = useState<boolean>(false);

  const handleToggleFeatured = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsFeatured(e.target.checked);
  };

  const handleCategoryChange = (event: any) => {
    setSelectedSubCategory("");
    setSelectedCategory(event.target.value as string);
  };

  const handleSubCategoryChange = (event: any) => {
    setSelectedSubCategory(event.target.value as string);
  };

  const handleAttributeSelect = (attributeId: string, value: string) => {
    setSelectedAttributes((prev) => {
      const current = prev[attributeId] || [];
      let updated = [];

      if (current.includes(value)) {
        // remove
        updated = current.filter((v) => v !== value);
      } else {
        // add
        updated = [...current, value];
      }

      const copy = { ...prev };

      if (updated.length === 0) {
        // 🔥 remove key if empty
        delete copy[attributeId];
      } else {
        copy[attributeId] = updated;
      }

      return copy;
    });
  };

  const generateCombinations = (attributes: any[]) => {
    return attributes.reduce(
      (acc, attr) =>
        acc.flatMap((x: any) =>
          attr.values.map((v: any) => [
            ...x,
            { attributeId: attr.attributeId, value: v },
          ])
        ),
      [[]]
    );
  };

  const updateVariantField = (index: number, field: string, value: any) => {
    setVariants((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const removeVariantMedia = (variantIndex: number, mediaIndex: number) => {
    setVariants((prev) => {
      const copy = [...prev];
      copy[variantIndex].media.splice(mediaIndex, 1);
      return copy;
    });
  };

  const handleCreateVariants = () => {
    const attributeArray = Object.keys(selectedAttributes).map((id) => ({
      attributeId: id,
      values: selectedAttributes[id],
    }));

    if (!Object.keys(selectedAttributes).length) {
      showError("Please select at least one attribute");
      return false;
    }

    const hasEmpty = Object.values(selectedAttributes).some(
      (values) => !values.length
    );

    if (hasEmpty) {
      showError("Please select at least one option for every attribute");
      return false;
    }

    const combinations = generateCombinations(attributeArray);

    if (!combinations.length) {
      showError("No valid variant combinations");
      return;
    }

    const newVariants = combinations.map((combo: any) => ({
      attributes: combo,
      variantName: combo.map((c: any) => c.value).join(" - "),
      price: "",
      discountedPrice: "",
      quantity: 1,
      maxPurchaseLimit: 1,
      thresholdLimit: 0,
      media: [],
    }));

    setVariants(newVariants); // 🔥 IMPORTANT: replace instead of append
  };

  const handleVariantImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    variantIndex: number
  ) => {
    const files = event.target.files;
    if (!files || !files.length) return;

    const uploadedMedia: {
      url: string;
      type: "image";
      isFeatured?: boolean;
    }[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const res = (await UploadMedia(file)) as any;
      if (res?.data) {
        uploadedMedia.push({
          url: res.data,
          type: "image",
        });
      }
    }

    if (!uploadedMedia.length) return;

    setVariants((prev) => {
      const copy = [...prev];

      const existingMedia = copy[variantIndex].media || [];
      copy[variantIndex].media = [...existingMedia, ...uploadedMedia];
      return copy;
    });
    setOpenModal(false);

    // reset input value so same image can be selected again
    event.target.value = "";
  };

  const validateVariants = () => {
    for (let i = 0; i < variants.length; i++) {
      const v = variants[i];

      if (!v.variantName.trim()) {
        showError(`Variant ${i + 1}: Name is required`);
        return false;
      }

      if (!v.price || Number(v.price) <= 0) {
        showError(`Variant ${i + 1}: Price must be greater than 0`);
        return false;
      }

      if (v.discountedPrice && Number(v.discountedPrice) > Number(v.price)) {
        showError(`Variant ${i + 1}: Discounted price cannot exceed price`);
        return false;
      }

      if (!v.quantity || v.quantity <= 0) {
        showError(`Variant ${i + 1}: Quantity is required`);
        return false;
      }

      if (!v.media.length) {
        showError(`Variant ${i + 1}: At least one image is required`);
        return false;
      }
    }

    return true;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: productData?.name || "",
      description: productData?.description || "",
      tags: productData?.tags[0] || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required().required("Name is required"),
      description: Yup.string().required().required("Description is required"),
      tags: Yup.string().required().required("Tags is required"),
    }),

    onSubmit: async (values) => {
      formik.setSubmitting(true);

      if (!variants?.length) {
        showError("Please add variants");
        return;
      }
      if (!validateVariants()) {
        return;
      }

      const body = {
        name: values.name,
        tags: [values.tags],
        categoryId: selectedCategory,
        subCategoryId: selectedSubCategory,
        description: values.description,
        variants: variants,
        deliveryAndReturns: vendorData?.returnWithIn,
      };
      if (id) {
        try {
          setLoading(true);
          const response = await updateProduct({ body, id }).unwrap();
          setLoading(false);
          if (response?.statusCode === 200) {
            showToast(response?.message || "");
            navigate(-1);
          } else {
            showError(response?.message || "");
          }
        } catch (error: any) {
          setLoading(false);
          showError(error?.data?.message || "");
          console.error(error);
        }
      } else {
        try {
          setLoading(true);
          const response = await addProduct(body).unwrap();
          setLoading(false);
          if (response?.statusCode === 200) {
            showToast(response?.message || "");
            navigate(-1);
          } else {
            showError(response?.message || "");
          }
        } catch (error: any) {
          setLoading(false);
          showError(error?.data?.message || "");
          console.error(error);
        }
      }
    },
  });

  const getAttributesApi = async () => {
    setLoading(true);
    try {
      const response = await getAttribute({
        categoryId: selectedCategory,
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setAttributes(response?.data || []);
      } else {
        setAttributes([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getCategoryApi = async () => {
    setLoading(true);
    try {
      const response = await getCategory({}).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setCategories(response?.data?.data || []);
      } else {
        setCategories([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getSubCategoryApi = async () => {
    setLoading(true);
    try {
      const response = await getSubCategory({
        parentId: selectedCategory,
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setSubCategories(response?.data?.data || []);
      } else {
        setSubCategories([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const mapApiVariantsToUI = (apiVariants: any[]) => {
    return apiVariants.map((v) => ({
      attributes: v?.attributes?.map((attr: any) => ({
        attributeId: attr?.attribute?.id,
        value: attr?.value,
      })),
      variantName: v?.variantName || "",
      price: String(v?.price ?? ""),
      discountedPrice: String(v?.discountedPrice ?? ""),
      quantity: v?.quantity ?? 1,
      maxPurchaseLimit: v?.maxPurchaseLimit ?? 1,
      thresholdLimit: v?.thresholdLimit ?? 0,
      media: (v?.media || []).map((m: any) => ({
        url: m?.url,
        type: m?.type,
      })),
    }));
  };

  const getProfileAPi = async () => {
    setLoading(true);
    try {
      const response = await getProfile({
        id: parsedUserData?.id,
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setVendorData(response?.data || null);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getProductDetailApi = async () => {
    setLoading(true);
    try {
      const response = await getProductById({ productId: id }).unwrap();
      setLoading(false);

      if (response.statusCode === 200) {
        const product = response.data;

        setProductData(product);
        setSelectedCategory(product?.category?.id || "");
        setSelectedSubCategory(product?.subCategory?.id || "");

        // ✅ PREFILL VARIANTS
        if (product?.variants?.length) {
          const mappedVariants = mapApiVariantsToUI(product.variants);
          setVariants(mappedVariants);
        }
      } else {
        setProductData(null);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getCategoryApi();
    getProfileAPi();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      getSubCategoryApi();
      getAttributesApi();
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (id) {
      getProductDetailApi();
    }
  }, []);

  return (
    <>
      <Loader isLoad={loading} />
      {/* 🟦 Page Header */}
      <div className="main_title">
        <h1>Add Product</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-products")}>
            Manage Products
          </button>
          - Add Product
        </p>
      </div>

      {/* 🟦 Product Form */}
      <div className="cards custom_form_details">
        <div className="form">
          <div className="gap_m w_100">
            <div className="control_group w_33">
              <label>Product Name</label>
              <TextField
                id="name"
                name="name"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                className="text_field"
                value={formik.values.name}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.name && typeof formik.errors.name === "string"
                    ? formik.errors.name
                    : ""
                }
              />
            </div>

            <div className="control_group w_33">
              <label>
                Product Category{" "}
                <img
                  className="cr_pt"
                  src="/static/images/ai_icon.svg"
                  alt="icon"
                />{" "}
              </label>
              <Select
                fullWidth
                displayEmpty
                value={selectedCategory}
                onChange={handleCategoryChange}
              >
                <MenuItem disabled value="">
                  Select Category
                </MenuItem>

                {categories?.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item?.name || "-"}
                  </MenuItem>
                ))}
              </Select>
              {error && !selectedCategory ? (
                <h6 className="err_msg">Category is required</h6>
              ) : (
                ""
              )}
            </div>

            <div className="control_group w_33">
              <label>
                Product Sub-Category{" "}
                <img
                  className="cr_pt"
                  src="/static/images/ai_icon.svg"
                  alt="icon"
                />
              </label>
              <Select
                fullWidth
                displayEmpty
                disabled={!selectedCategory}
                value={selectedSubCategory}
                onChange={handleSubCategoryChange}
              >
                <MenuItem disabled value="">
                  Select Sub-Category
                </MenuItem>
                {subCategories?.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item?.name || "-"}
                  </MenuItem>
                ))}
              </Select>
              {error && !selectedSubCategory ? (
                <h6 className="err_msg">Sub-Category is required</h6>
              ) : (
                ""
              )}
            </div>

            <div className="control_group w_33">
              <label>
                Tags{" "}
                <img
                  className="cr_pt"
                  src="/static/images/ai_icon.svg"
                  alt="icon"
                />
              </label>
              <TextField
                id="tags"
                name="tags"
                placeholder="Enter here"
                fullWidth
                hiddenLabel
                className="text_field"
                value={formik.values.tags}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.tags && typeof formik.errors.tags === "string"
                    ? formik.errors.tags
                    : ""
                }
              />
            </div>
            <div className="control_group w_100 ai_rec_btn">
              <label>Product Description</label>
              <TextField
                id="description"
                name="description"
                className="text_field"
                fullWidth
                multiline
                minRows={4}
                placeholder="Enter product description..."
                value={formik.values.description}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.description &&
                  typeof formik.errors.description === "string"
                    ? formik.errors.description
                    : ""
                }
              />
              <Button className="btn btn_primary sm">
                <img src="/static/images/ai_icon.svg" alt="" />
                Enhance with AI
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* 🟦 Attributes Section */}
      <div className="cards variants">
        <Box sx={{ borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Add Attributes
          </Typography>

          {/* ✅ Color Chips */}
          {attributes?.map((item) => {
            return (
              <div className="select_variant">
                <Typography variant="subtitle1" fontWeight={500}>
                  <SwitchToggle /> {item?.title || "-"}
                </Typography>

                <Box display="flex" gap={1} mt={1}>
                  {item?.options?.map((color) => {
                    const isSelected =
                      selectedAttributes[item.id]?.includes(color) || false;
                    return (
                      <Chip
                        key={color}
                        label={color}
                        onClick={() => handleAttributeSelect(item.id, color)}
                        clickable
                        sx={{
                          backgroundColor: isSelected
                            ? "#D3B88C"
                            : "transparent",
                          color: isSelected ? "#fff" : "inherit",
                          borderColor: "#D3B88C",
                        }}
                      />
                    );
                  })}
                </Box>
              </div>
            );
          })}
        </Box>

        <Button
          disabled={!attributes?.length}
          className="btn btn_primary"
          onClick={handleCreateVariants}
        >
          Create Variant
        </Button>

        {variants?.map((variant, vIndex) => (
          <div key={vIndex} className="create_variant mt_20">
            {/* ATTRIBUTES HEADER */}
            <ul>
              {variant.attributes.map((attr) => {
                const title =
                  attributes.find((a) => a?.id === attr?.attributeId)?.title ||
                  "-";
                return (
                  <li key={attr?.attributeId}>
                    <strong>{title}</strong> {attr?.value}
                  </li>
                );
              })}

              <li>
                <Button
                  onClick={() =>
                    setVariants((prev) => prev.filter((_, i) => i !== vIndex))
                  }
                >
                  <DeleteIcon />
                </Button>
              </li>
            </ul>

            {/* FORM */}
            <div className="form">
              <div className="gap_m">
                <div className="control_group w_33">
                  <label>Variant Name</label>
                  <TextField
                    value={variant?.variantName}
                    placeholder="Variant Name"
                    onChange={(e) =>
                      updateVariantField(vIndex, "variantName", e.target.value)
                    }
                    fullWidth
                  />
                </div>

                <div className="control_group w_33">
                  <label>Price</label>
                  <TextField
                    value={variant.price}
                    placeholder="Price"
                    onChange={(e) => {
                      const { value } = e.target;
                      if (isNumber(value)) {
                        updateVariantField(vIndex, "price", e.target.value);
                      }
                    }}
                    fullWidth
                  />
                </div>

                <div className="control_group w_33">
                  <label>Discounted Price</label>
                  <TextField
                    value={variant?.discountedPrice}
                    placeholder="Discounted Price"
                    onChange={(e) => {
                      const { value } = e.target;
                      if (isNumber(value)) {
                        updateVariantField(
                          vIndex,
                          "discountedPrice",
                          e.target.value
                        );
                      }
                    }}
                    fullWidth
                  />
                </div>
              </div>
            </div>

            {/* MEDIA */}
            <Typography variant="subtitle1" fontWeight={500} mt={3}>
              Product Images or Videos*
            </Typography>

            <div className="gap_p">
              <div className="control_group wrap_viewDemo_steps w_100">
                <label>Upload</label>
                <div className="upload_box">
                  <label
                    className="upload_action"
                    onClick={() => {
                      setSelectedIndex(vIndex);
                      setOpenModal(true);
                    }}
                    style={{ minHeight: "195px" }}
                  >
                    <p>
                      <img src="/static/images/upload_icon.svg" alt="icon" />
                      <strong>Click to upload image</strong>
                    </p>
                  </label>
                </div>
              </div>

              {variant.media.map((m, mIndex) => (
                <div key={mIndex} className="control_group w_17">
                  <div className="upload_image">
                    <div className="upload_image_holder square">
                      <figure>
                        <CardMedia component="img" image={m.url} />
                      </figure>

                      {/* {m.isFeatured && (
                        <span className="featured_tag">Featured</span>
                      )} */}

                      <CloseIcon
                        onClick={() => removeVariantMedia(vIndex, mIndex)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="cards">
        <Grid item xs={12}>
          <Box display="flex" gap={2} flexDirection="column">
            <Box display="flex" gap={2}>
              Add Product as Featured Products
              <SwitchToggle
                checked={isFeatured}
                onChange={handleToggleFeatured}
              />
            </Box>

            {isFeatured && (
              <div className="flex_clm">
                <Typography>
                  One-time fee AED 300. Featured for 7 days from the moment this
                  product goes live after approval.
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ marginLeft: "4px" }}
                >
                  Estimated period: 12 Dec 2025 – 19 Dec 2025
                  <br />
                </Typography>
                <div className="btn_row">
                  <Button className="btn btn_primary sm mt_20">
                    Confirm & Add as featured
                  </Button>
                  <Button className="btn btn_primary sm mt_20">Cancel</Button>
                </div>
              </div>
            )}
          </Box>
        </Grid>
      </div>
      <div className="cards">
        <Box sx={{ borderRadius: 2 }}>
          <Typography variant="h5" fontWeight={600} mb={2}>
            Product Promotion{" "}
            <Typography variant="h6" fontWeight={400}>
              {" "}
            </Typography>
          </Typography>

          <Box
            sx={{
              background: "#D3B88C",
              p: 2,
              color: "#fff",
              borderRadius: 2,
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CommentIcon />
            <Typography style={{ color: "#fff" }}>
              To promote your product you need to upgrade your subscription
            </Typography>
          </Box>
        </Box>
        <div className="form mt_20">
          {/* 🟦 Product Images */}
          <Typography variant="subtitle1" fontWeight={500} mt={3}>
            Product Reels{" "}
            <img className="cr_pt" src="/static/images/ai_icon.svg" alt="" />
          </Typography>
          <div className="gap_p ">
            <div className="control_group wrap_viewDemo_steps w_100">
              <div className="upload_box">
                <label
                  className="upload_action"
                  onClick={() => setOpenModal(true)}
                  style={{ minHeight: "195px" }}
                >
                  <p>
                    <img src="/static/images/upload_icon.svg" alt="icon" />
                    <strong>Click to upload image or take photo</strong>
                  </p>
                </label>
              </div>
            </div>
            <div className="control_group w_17">
              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={image} alt="photo" />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImage("");
                        setFileName("");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <span className="upload_image_holder square">
                    <figure className="add_img">
                      <img src="/static/images/placeholder.png" alt="icon" />
                      <span className="upload_img_icon">
                        <img src="/static/images/trash2_icon.svg" alt="icon" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>
            <div className="control_group w_17">
              <label htmlFor="icon-button-file"></label>
              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={image} alt="photo" />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImage("");
                        setFileName("");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <span className="upload_image_holder square">
                    <figure className="add_img">
                      <img src="/static/images/placeholder.png" alt="icon" />
                      <span className="upload_img_icon">
                        <img src="/static/images/trash2_icon.svg" alt="icon" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>
            <div className="control_group w_17">
              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={image} alt="photo" />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImage("");
                        setFileName("");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <span className="upload_image_holder square">
                    <figure className="add_img">
                      <img src="/static/images/placeholder.png" alt="icon" />
                      <span className="upload_img_icon">
                        <img src="/static/images/trash2_icon.svg" alt="icon" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="w_100 mt_20">
        <Button
          className="w_100 btn btn_primary"
          size="large"
          onClick={() => {
            formik.submitForm();
            setError(true);
          }}
        >
          Save
        </Button>
      </div>
      <SelectUpload
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        onUpload={(e) => handleVariantImageUpload(e, selectedIndex)}
        multiple
      />
    </>
  );
};

export default AddProductEnhanced;
